import _ from 'lodash';
import moment from 'moment'
import type CartServiceInfo from '~/domains/payment/cart/CartServiceInfo';
import type ProgramInfo from '~/domains/program/ProgramInfo';
import type ProgramCodeInfo from '~/domains/program/code/ProgramCodeInfo';
import type NdcInfoState from '~/domains/store/ndcInfo/NdcInfoState';
import type OrderInfoState from '~/domains/store/orderInfo/OrderInfoState';
import type ServiceState from "~/domains/store/services/ServiceState";
import type UniversalPricingState from '~/domains/store/services/UniversalPricingState';

export function roundPrice(value: number, direction: "up" | "down" | "nearest" = "nearest", precision: number = 2) {
  const factor = Math.pow(10, precision);

  switch (direction) {
    case "up":
      return Math.ceil(value * factor) / factor;
    case "down":
      return Math.floor(value * factor) / factor;
    default:
      return Math.round(value * factor) / factor;
  }
}

export function getFormattedPrice(value: number) {
  if (typeof value !== "number" || isNaN(value)) {
    return undefined;
  }

  const formattedValue = (Math.abs(value)).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return value >= 0 ? `$${formattedValue}` : `-$${formattedValue}`;
}

export function getLawnCareProgramServiceIds() {
  return [2, 3];
}

export function isServiceLawnCareProgram(service: ServiceState): boolean {
  return getLawnCareProgramServiceIds().includes(service.ServiceDefId);
}

export function isServiceWebDiscountEligible(service: ServiceState): boolean {
  return !getLawnCareProgramServiceIds().includes(service.ServiceDefId);
}

export function getServiceSeasonYear(referenceDate: Date) {
  return getServiceSeasonEndDate(referenceDate).getFullYear();
}

export function getServiceSeasonEndDate(referenceDate: Date): Date {
  // Billing year rolls over at midnight December 1st local time
  const serviceSeasonEndMonthIndex = (12 - 1); // Zero-based index
  const serviceSeasonYearEndDay = 1;
  const currentBillingYear = (referenceDate.getMonth() >= serviceSeasonEndMonthIndex && referenceDate.getDate() >= serviceSeasonYearEndDay)
    ? referenceDate.getFullYear() + 1
    : referenceDate.getFullYear();

  return new Date(currentBillingYear, serviceSeasonEndMonthIndex, serviceSeasonYearEndDay);
}

export function getMonthlyBillingMonthsRemaining(referenceDate: Date): number {
  const serviceSeasonEndMoment = moment(getServiceSeasonEndDate(referenceDate));

  const monthlyBillingCutoffDay = 1;
  const seasonMonthlyEndMoment = moment([serviceSeasonEndMoment.year(), serviceSeasonEndMoment.month(), monthlyBillingCutoffDay]);

  return Math.min(seasonMonthlyEndMoment.diff(moment(referenceDate), "months") + 1, 12);
}

export function getInstallmentBillingMonthsRemaining(referenceDate: Date, order?: OrderInfoState): number {
  /* Attempt to substitute remaining months information from the current order, if available. */
  if (order && _.isArray(order.orderItems)) {
    const minRemainingMonths = _.min(order.orderItems
      .filter(orderItem => orderItem.remainMonths !== null && orderItem.remainMonths !== undefined)
      .map(orderItem => orderItem.remainMonths)
    );
    if (minRemainingMonths) {
      return minRemainingMonths;
    }
  }

  const serviceSeasonEndMoment = moment(getServiceSeasonEndDate(referenceDate));
  
  const installmentBillingCutoffDay = 15;
  const seasonInstallmentEndMoment = moment([serviceSeasonEndMoment.year(), serviceSeasonEndMoment.month(), installmentBillingCutoffDay]);

  return Math.min(seasonInstallmentEndMoment.diff(moment(referenceDate), "months") + 1, 12);
}

export function getNdcDiscountDescription(ndcInfo: NdcInfoState) {
  const extractedWebTitle = ndcInfo.webTitle.match(/(<[^<>]+>)?([^<>]*)(<\/[^<>]+>)?/)?.at(2);

  return extractedWebTitle ?? ndcInfo.description;
}

export function getAccountProgramUniversalPricing(accountProgram: ProgramInfo, programCodes: ProgramCodeInfo[], franchiseServices: UniversalPricingState[]): UniversalPricingState | undefined {
  const programCode = programCodes.find((programCode) => programCode.programDefinitionID === accountProgram.programCodeId);

  if (!programCode) {
    return undefined;
  }

  return franchiseServices.find((franchiseService) => franchiseService.rgMapping === programCode.code);
}

export function isAccountProgramLawnCare(accountProgram: ProgramInfo, programCodes: ProgramCodeInfo[], franchiseServices: UniversalPricingState[]): boolean {
  const universalPricing = getAccountProgramUniversalPricing(accountProgram, programCodes, franchiseServices);
  return !!universalPricing && getLawnCareProgramServiceIds().includes(universalPricing.id);
}

export function getPrePayCode(prePayDiscountValue: number) {
  return `W${Math.round(prePayDiscountValue * 100).toString().padStart(2, "0")}`;
}

export function isServicePackage(service: CartServiceInfo): boolean {
  return service.isPackageItem;
}

export function isCartServiceSeasonal(service: CartServiceInfo): boolean {
  return !service.isPackageItem && !service.isMonthly && !service.isInitialPayment;
}

export function isCartServiceMonthly(service: CartServiceInfo): boolean {
  return !service.isPackageItem && service.isMonthly;
}

export function isCartServiceInitialPayment(service: CartServiceInfo): boolean {
  return !service.isPackageItem && !service.isMonthly && service.isInitialPayment;
}
